import React, { useState, useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';

const APPROVAL_CACHE_KEY = 'crypn_waitlist_approved';

/**
 * Read cached approval status from localStorage.
 * Returns true if the user was previously approved, false otherwise.
 */
function getCachedApproval(email: string): boolean {
  try {
    const cached = localStorage.getItem(APPROVAL_CACHE_KEY);
    if (!cached) return false;
    const parsed = JSON.parse(cached);
    return parsed.email === email && parsed.approved === true;
  } catch {
    return false;
  }
}

function setCachedApproval(email: string, approved: boolean): void {
  try {
    localStorage.setItem(APPROVAL_CACHE_KEY, JSON.stringify({ email, approved }));
  } catch {
    // localStorage unavailable — non-fatal
  }
}

const ProtectedRoute = ({ children }) => {
  const { user, loading: authLoading } = useAuth();
  const [waitlistStatus, setWaitlistStatus] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<any>(null);
  const location = useLocation();

  const checkWaitlistStatus = async (currentUser) => {
    // Check localStorage cache first — if approved before, trust it
    if (getCachedApproval(currentUser.email)) {
      setWaitlistStatus({ approved: true, email: currentUser.email });
      return;
    }

    try {
      // Query with timeout to prevent hanging
      const { data: waitlistEntry, error: waitlistError } = (await Promise.race([
        supabase
          .from('waitlist_crypn')
          .select('approved, user_id, email')
          .eq('email', currentUser.email)
          .maybeSingle(),
        new Promise((_, reject) =>
          setTimeout(() => reject(new Error('Waitlist query timeout after 5000ms')), 5000),
        ),
      ])) as any;

      if (waitlistError) {
        console.error('Waitlist query error:', waitlistError);
        setWaitlistStatus(null);
        setError('Unable to verify waitlist status');
      } else if (waitlistEntry) {
        setWaitlistStatus(waitlistEntry);
        // Cache approval so we don't re-query on every page load
        if (waitlistEntry.approved) {
          setCachedApproval(currentUser.email, true);
        }
      } else {
        // User not found in waitlist
        setWaitlistStatus(null);
        setError('User not found in waitlist');
      }
    } catch (err: any) {
      console.error('Error checking waitlist status:', err);
      // On timeout/network error, don't kick the user out — they may have been
      // approved before and the query just failed transiently
      setWaitlistStatus(null);
      setError(err.message || 'Failed to verify access');
    }
  };

  useEffect(() => {
    let mounted = true;

    const verifyAccess = async () => {
      if (!user) {
        if (mounted) {
          setLoading(false);
        }
        return;
      }

      try {
        await checkWaitlistStatus(user);
      } catch (err: any) {
        if (mounted) {
          setError(err.message);
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    if (!authLoading) {
      verifyAccess();
    }

    return () => {
      mounted = false;
    };
  }, [user, authLoading]);

  // Show loading while auth or waitlist status is being checked
  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-dark-charcoal flex items-center justify-center">
        <div className="flex flex-col items-center space-y-4">
          <div className="flex items-center space-x-3">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-vibrant-green"></div>
            <span className="text-white text-lg">Verifying access...</span>
          </div>
          {error && (
            <div className="bg-red-900/20 border border-red-500/30 rounded-lg p-4 max-w-md">
              <p className="text-red-400 text-sm text-center">{error}</p>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Not logged in - redirect to auth page
  if (!user) {
    return <Navigate to="/auth" state={{ from: location }} replace />;
  }

  // If waitlist query failed but user was previously approved (cached), let them through
  if (!waitlistStatus && user.email && getCachedApproval(user.email)) {
    return children;
  }

  // User exists but waitlist status couldn't be determined or user not approved
  if (!waitlistStatus || !waitlistStatus.approved) {
    return <Navigate to="/waitlist" replace />;
  }

  // User is approved - render protected content
  return children;
};

export default ProtectedRoute;
